import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Trash2, Ban, Eye, User, Upload, Edit3, ChevronDown, ChevronUp, MapPin, FileText } from 'lucide-react';
import { ORDER_STATUS_LABELS, ORDER_STATUS_COLORS, ORDER_STATUS, PROSTHESIS_TYPE_LABELS, getSubtypeLabel } from '../../utils/constants';
import StatusBadge from './StatusBadge';
import StatusSelect from '../prosthetist/StatusSelect';
import FilePreview from './FilePreview';
import OrderDiscussion from './OrderDiscussion';
import InternalNotes from '../prosthetist/InternalNotes';
import FileUploadZone from './FileUploadZone';
import OrderForm from '../client/OrderForm';
import { filesApi, ordersApi } from '../../services/api';
import { formatPatientDisplay } from '../../utils/nameNormalizer';
import { canEditOrder, getEditableFields } from '../../utils/permissions';

const ANIMATION_DURATION = 150;

const OrderDetails = ({ order, onClose, onDeleteOrder, onSendMessage, onMarkMessagesAsRead, onWebSocketMessage, currentUserType = 'client', currentUserName = 'Dr. Martin Dubois', currentUserId = null, onPatientClick, onFilesUploaded, onOrderUpdated, onStatusChange }) => {
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [currentOrder, setCurrentOrder] = useState(order);
    const [newFiles, setNewFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [wasEdited, setWasEdited] = useState(false);
    const [showBillingAddress, setShowBillingAddress] = useState(false);
    const [downloadingInvoice, setDownloadingInvoice] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [deletingFileId, setDeletingFileId] = useState(null);
    const markedOrderId = useRef(null);
    const prevOrderIdRef = useRef(order.id);

    // Check if user can edit this order
    const userCanEdit = canEditOrder(currentOrder, currentUserType);
    const editableFields = getEditableFields(currentUserType);

    // Téléchargement direct du PDF de la facture couvrant cette commande.
    const handleDownloadInvoice = async () => {
        setDownloadingInvoice(true);
        try {
            const blob = await ordersApi.downloadInvoiceBlob(currentOrder.id);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `facture-commande-${currentOrder.id}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            alert('Échec du téléchargement de la facture.');
        } finally {
            setDownloadingInvoice(false);
        }
    };

    // Mettre à jour l'ordre local quand on change de commande OU quand les messages changent
    useEffect(() => {
        if (prevOrderIdRef.current !== order.id) {
            // Nouvelle commande : réinitialiser complètement
            setCurrentOrder(order);
            prevOrderIdRef.current = order.id;
        } else {
            // Même commande : fusionner les messages pour ne pas perdre les mises à jour
            setCurrentOrder(prev => {
                const prevMessages = prev.discussion?.messages || [];
                const newMessages = order.discussion?.messages || [];

                // Si les messages de order sont plus récents, fusionner
                if (newMessages.length > prevMessages.length) {
                    const existingIds = new Set(prevMessages.map(m => m.id));
                    const additionalMessages = newMessages.filter(m => !existingIds.has(m.id));
                    if (additionalMessages.length > 0) {
                        return {
                            ...prev,
                            discussion: {
                                ...prev.discussion,
                                messages: [...prevMessages, ...additionalMessages],
                                lastActivity: order.discussion?.lastActivity || prev.discussion?.lastActivity
                            }
                        };
                    }
                }
                return prev;
            });
        }
    }, [order]);

    // Auto-mark messages as read when modal opens (only once per order)
    useEffect(() => {
        if (onMarkMessagesAsRead && currentUserType && order && markedOrderId.current !== order.id) {
            markedOrderId.current = order.id;
            onMarkMessagesAsRead(order.id, currentUserType);

            // Mettre à jour localement les messages pour les marquer comme lus
            if (order.discussion?.messages) {
                const updatedMessages = order.discussion.messages.map(msg => {
                    // Marquer comme lu les messages de l'autre utilisateur
                    if (msg.senderType !== currentUserType) {
                        return { ...msg, isRead: true };
                    }
                    return msg;
                });

                setCurrentOrder(prev => ({
                    ...prev,
                    discussion: {
                        ...prev.discussion,
                        messages: updatedMessages
                    }
                }));
            }
        }
    }, [order.id, currentUserType, onMarkMessagesAsRead]); // Se déclenche quand l'ID de la commande change
    
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getProsthesisTypeDisplay = (order) => {
        const typeLabel = PROSTHESIS_TYPE_LABELS[order.prosthesisType] || order.prosthesisType;
        const subtypeLabel = order.prosthesisSubtype
            ? getSubtypeLabel(order.prosthesisType, order.prosthesisSubtype)
            : null;
        return subtypeLabel ? `${typeLabel} - ${subtypeLabel}` : typeLabel;
    };

    const getWorkTypeDisplay = (work) => {
        const typeLabel = PROSTHESIS_TYPE_LABELS[work.prosthesisType] || work.prosthesisType;
        const subtypeLabel = work.prosthesisSubtype
            ? getSubtypeLabel(work.prosthesisType, work.prosthesisSubtype)
            : null;
        return subtypeLabel ? `${typeLabel} - ${subtypeLabel}` : typeLabel;
    };

    const getFileIcon = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        if (extension === 'stl' || extension === 'ply') {
            return (
                <svg className="h-5 w-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
            );
        }
        if (extension === 'zip') {
            return (
                <svg className="h-5 w-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h2m-2 4h2m-2 4h2m4-12h2m-2 4h2m-2 4h2m-2 4h2M3 4a2 2 0 012-2h14a2 2 0 012 2v16a2 2 0 01-2 2H5a2 2 0 01-2-2V4z" />
                </svg>
            );
        }
        return (
            <svg className="h-5 w-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
        );
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = () => {
        onDeleteOrder(currentOrder.id);
        setShowDeleteConfirm(false);
        handleClose();
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirm(false);
    };

    // Le prothésiste/admin peut changer le statut directement depuis le détail.
    const canChangeStatus = !!onStatusChange && ['prosthetist', 'admin'].includes(currentUserType);
    const handleStatusChange = async (newStatus) => {
        setCurrentOrder(prev => ({ ...prev, status: newStatus }));
        try {
            await onStatusChange(currentOrder.id, newStatus);
        } catch (error) {
            console.error('Erreur lors du changement de statut:', error);
            setCurrentOrder(prev => ({ ...prev, status: order.status }));
        }
    };

    const handlePreviewFile = (file) => {
        setPreviewFile(file);
    };

    const handleClosePreview = () => {
        setPreviewFile(null);
    };

    const handleSendMessage = async (orderId, messageData) => {
        try {
            const newMessage = await onSendMessage(orderId, messageData);
            if (newMessage) {
                // Mettre à jour l'ordre local immédiatement
                setCurrentOrder(prevOrder => ({
                    ...prevOrder,
                    discussion: {
                        ...prevOrder.discussion,
                        messages: [...(prevOrder.discussion?.messages || []), newMessage],
                        lastActivity: newMessage.timestamp
                    }
                }));
            }
            return newMessage;
        } catch (error) {
            console.error('Erreur lors de l\'envoi du message:', error);
            throw error;
        }
    };

    const handleDownloadFile = async (file) => {
        if (!file.id) {
            console.log(`Impossible de télécharger le fichier: ${file.name} - ID manquant`);
            return;
        }

        try {
            console.log('Téléchargement du fichier avec authentification:', file.id);
            
            // Utiliser l'API authentifiée pour télécharger le fichier
            const blob = await filesApi.downloadBlob(file.id);
            
            // Créer une URL locale à partir du blob et déclencher le téléchargement
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = file.name || 'fichier';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Nettoyer l'URL blob après utilisation
            URL.revokeObjectURL(blobUrl);
            
            console.log('Fichier téléchargé avec succès');
        } catch (error) {
            console.error('Erreur lors du téléchargement du fichier:', error);
        }
    };

    const isPreviewable = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'stl'].includes(extension);
    };

    const handleEditSuccess = (updatedOrder) => {
        setIsEditing(false);
        setWasEdited(true);
        // Update local order with new data (includes system message)
        if (updatedOrder) {
            setCurrentOrder(updatedOrder);
        }
        // Note: Don't call onOrderUpdated here to avoid re-render cascade
        // The orders list will be refreshed when user closes the modal
    };

    const handleClose = useCallback(() => {
        if (isClosing) return;
        setIsClosing(true);

        setTimeout(() => {
            // Refresh orders list if order was edited
            if (wasEdited && onOrderUpdated) {
                onOrderUpdated(currentOrder.id);
            }
            onClose();
        }, ANIMATION_DURATION);
    }, [isClosing, wasEdited, onOrderUpdated, currentOrder.id, onClose]);

    const canDeleteFile = (file) => {
        if (currentUserType === 'prosthetist' || currentUserType === 'admin') return true;
        if (!currentUserId) return false;
        return file.uploadedBy === currentUserId;
    };

    const handleDeleteFile = async (file) => {
        if (!window.confirm(`Supprimer le fichier "${file.name}" ? Cette action est irréversible.`)) {
            return;
        }

        setDeletingFileId(file.id);
        try {
            await filesApi.delete(file.id);
            setCurrentOrder(prev => ({
                ...prev,
                files: (prev.files || []).filter(f => f.id !== file.id),
            }));
            if (onFilesUploaded) {
                onFilesUploaded(currentOrder.id);
            }
        } catch (error) {
            console.error('Erreur lors de la suppression du fichier:', error);
            window.alert(error?.message || 'Impossible de supprimer le fichier.');
        } finally {
            setDeletingFileId(null);
        }
    };

    const handleUploadFiles = async () => {
        if (newFiles.length === 0) {
            return;
        }

        setIsUploading(true);
        setUploadError(null);
        setUploadSuccess(false);

        try {
            // Upload files via API
            const uploadedFiles = await filesApi.upload(currentOrder.id, newFiles);

            console.log('Files uploaded successfully:', uploadedFiles);

            // Update local order with new files
            setCurrentOrder(prevOrder => ({
                ...prevOrder,
                files: [...prevOrder.files, ...uploadedFiles]
            }));

            // Clear the upload form
            setNewFiles([]);
            setUploadSuccess(true);

            // Notify parent component if callback provided
            if (onFilesUploaded) {
                onFilesUploaded(currentOrder.id);
            }

            // Hide success message after 3 seconds
            setTimeout(() => setUploadSuccess(false), 3000);

        } catch (error) {
            console.error('Error uploading files:', error);
            setUploadError('Erreur lors de l\'upload des fichiers. Veuillez réessayer.');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div
            className={`fixed inset-0 overlay-frost overflow-y-auto h-full w-full z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
            onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
            <div className={`relative top-10 mx-auto p-5 border border-white/10 w-11/12 md:w-5/6 lg:w-4/5 xl:w-3/4 shadow-lg rounded-md glass-deep max-h-[90vh] overflow-y-auto ${isClosing ? 'animate-modal-out' : 'animate-modal-in'}`}>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-white">
                        {isEditing ? 'Modifier la commande' : 'Détails de la commande'} #{currentOrder.id}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-200"
                    >
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Colonne gauche - Informations de la commande ou Formulaire d'édition */}
                    <div className="space-y-6">
                    {isEditing ? (
                        <div className="bg-black/30 border border-white/10 rounded-lg">
                            <OrderForm
                                mode="edit"
                                initialData={currentOrder}
                                editableFields={editableFields}
                                userType={currentUserType}
                                onSubmitSuccess={handleEditSuccess}
                                onCancel={() => setIsEditing(false)}
                            />
                        </div>
                    ) : (
                    <>
                    {/* En-tête avec statut */}
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-white">
                                {currentOrder.works?.length || 0} travail{(currentOrder.works?.length || 0) > 1 ? 'x' : ''}
                            </h3>
                            <p className="text-sm text-gray-400">
                                Commande du {formatDate(currentOrder.orderDate)}
                            </p>
                        </div>
                        {canChangeStatus ? (
                            <StatusSelect
                                status={currentOrder.status}
                                onChange={handleStatusChange}
                            />
                        ) : (
                            <StatusBadge
                                status={currentOrder.status}
                                label={ORDER_STATUS_LABELS[currentOrder.status]}
                                colorClass={ORDER_STATUS_COLORS[currentOrder.status]}
                            />
                        )}
                    </div>

                    {/* Informations du client */}
                    <div className="bg-black/30 border border-white/10 rounded-lg p-4">
                        <h4 className="text-md font-semibold text-white mb-3">Informations du client</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <p className="text-sm font-medium text-gray-400">Nom</p>
                                <p className="text-sm text-gray-200">{currentOrder.clientInfo.name}</p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-400">Cabinet</p>
                                <p className="text-sm text-gray-200">{currentOrder.clientInfo.clinic}</p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-400">Téléphone</p>
                                <p className="text-sm text-gray-200">{currentOrder.clientInfo.phone}</p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-400">Email</p>
                                <p className="text-sm text-gray-200">{currentOrder.clientInfo.email}</p>
                            </div>
                            <div className="md:col-span-2">
                                <p className="text-sm font-medium text-gray-400">Adresse</p>
                                <p className="text-sm text-gray-200">{currentOrder.clientInfo.address}</p>
                            </div>
                        </div>
                    </div>

                    {/* Adresse de facturation (collapsible) */}
                    {currentOrder.billing_name && (
                        <div className="bg-black/30 border border-white/10 rounded-lg overflow-hidden">
                            <button
                                onClick={() => setShowBillingAddress(!showBillingAddress)}
                                className="w-full flex items-center justify-between p-4 text-left hover:bg-white/[0.08] transition-colors"
                            >
                                <div className="flex items-center">
                                    <MapPin className="w-5 h-5 text-blue-400 mr-2" />
                                    <h4 className="text-md font-semibold text-white">Adresse de facturation</h4>
                                </div>
                                {showBillingAddress ? (
                                    <ChevronUp className="w-5 h-5 text-gray-400" />
                                ) : (
                                    <ChevronDown className="w-5 h-5 text-gray-400" />
                                )}
                            </button>

                            {showBillingAddress && (
                                <div className="px-4 pb-4 border-t border-white/10">
                                    <div className="pt-3 text-sm text-gray-200 space-y-1">
                                        <p className="font-medium">{currentOrder.billing_name}</p>
                                        <p>{currentOrder.billing_address_line1}</p>
                                        {currentOrder.billing_address_line2 && (
                                            <p>{currentOrder.billing_address_line2}</p>
                                        )}
                                        <p>{currentOrder.billing_postal_code} {currentOrder.billing_city}</p>
                                        <p>{currentOrder.billing_country || 'France'}</p>
                                        {currentOrder.billing_siret && (
                                            <p className="text-gray-400">SIRET/SIREN : {currentOrder.billing_siret}</p>
                                        )}
                                        {currentOrder.billing_vat_number && (
                                            <p className="text-gray-400">N° TVA : {currentOrder.billing_vat_number}</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Informations du patient */}
                    {currentOrder.patient_name_code && (
                        <div className="bg-yellow-600/10 border border-yellow-600/30 rounded-lg p-4">
                            <h4 className="text-md font-semibold text-yellow-400 mb-3">Patient</h4>
                            <div className="flex items-center space-x-2">
                                <User className="h-5 w-5 text-yellow-400" />
                                <div>
                                    {onPatientClick ? (
                                        <button
                                            onClick={() => onPatientClick({
                                                patientNameCode: currentOrder.patient_name_code,
                                                clientName: currentOrder.client_name
                                            })}
                                            className="text-lg font-semibold text-yellow-400 hover:text-yellow-300 hover:underline cursor-pointer transition-colors"
                                            title="Voir l'historique des commandes pour ce patient"
                                        >
                                            {currentOrder.patient_name_code}
                                        </button>
                                    ) : (
                                        <span className="text-lg font-semibold text-yellow-400">
                                            {currentOrder.patient_name_code}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Travaux */}
                    <div className="bg-black/30 border border-white/10 rounded-lg p-4">
                        <h4 className="text-md font-semibold text-white mb-3">
                            Travaux ({currentOrder.works?.length || 0})
                        </h4>
                        <div className="space-y-3">
                            {currentOrder.works?.map((work, index) => (
                                <div key={index} className="border border-white/15 rounded-lg overflow-hidden bg-gradient-to-r from-gray-800/50 to-gray-900/50">
                                    {/* Header avec type */}
                                    <div className="bg-gradient-to-r from-indigo-900/40 to-purple-900/30 px-4 py-2 border-b border-white/15">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-medium text-indigo-300 uppercase tracking-wide">
                                                Travail {index + 1}
                                            </span>
                                            <span className="text-sm font-semibold text-white">
                                                {getWorkTypeDisplay(work)}
                                            </span>
                                        </div>
                                    </div>
                                    {/* Contenu */}
                                    <div className="px-4 py-3">
                                        {/* Badges info */}
                                        <div className="flex flex-wrap items-center gap-2 mb-3">
                                            <span className="inline-flex items-center gap-1.5 bg-white/15/60 px-2.5 py-1 rounded-full text-xs">
                                                <svg className="w-3.5 h-3.5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                                </svg>
                                                <span className="text-gray-200 font-medium">
                                                    {work.toothNumbers?.length > 1 ? 'Dents' : 'Dent'} {work.toothNumbers?.join(', ') || '-'}
                                                </span>
                                            </span>
                                            {work.shade && (
                                                <span className="inline-flex items-center gap-1.5 bg-blue-900/40 px-2.5 py-1 rounded-full text-xs">
                                                    <svg className="w-3.5 h-3.5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                                                    </svg>
                                                    <span className="text-blue-200 font-semibold">{work.shade}</span>
                                                </span>
                                            )}
                                            {work.hasDigitalModel && (
                                                <span className="inline-flex items-center gap-1.5 bg-yellow-900/40 px-2.5 py-1 rounded-full text-xs">
                                                    <svg className="w-3.5 h-3.5 text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                    </svg>
                                                    <span className="text-yellow-200 font-semibold">Modèle 3D</span>
                                                </span>
                                            )}
                                        </div>
                                        {/* Description */}
                                        {work.description && (
                                            <p className="text-sm text-gray-300 leading-relaxed">
                                                {work.description}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Date de livraison */}
                    <div className="bg-black/30 border border-white/10 rounded-lg p-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <p className="text-sm font-medium text-gray-400">Date de livraison souhaitée</p>
                                <p className="text-sm text-gray-200">{formatDate(currentOrder.deliveryDate)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Fichiers joints */}
                    <div>
                        <h4 className="text-md font-semibold text-white mb-3">Fichiers joints</h4>
                        <div className="space-y-2">
                            {currentOrder.files.map((file, index) => (
                                <div key={index} className="flex items-center space-x-3 p-3 bg-black/30 border border-white/10 rounded-md">
                                    {getFileIcon(file.name)}
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-200">{file.name}</p>
                                        <p className="text-xs text-gray-500">{file.size}</p>
                                        {file.uploaderName && (
                                            <p className="text-xs text-gray-500 mt-1">
                                                Envoyé par: {file.uploaderName}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        {isPreviewable(file.name) && (
                                            <button
                                                onClick={() => handlePreviewFile(file)}
                                                className="text-yellow-400 hover:text-yellow-300 text-sm font-medium flex items-center space-x-1 px-2 py-1 rounded hover:bg-yellow-500/20 transition-colors"
                                                title="Prévisualiser"
                                            >
                                                <Eye className="h-4 w-4" />
                                                <span>Aperçu</span>
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleDownloadFile(file)}
                                            className="text-green-400 hover:text-green-300 text-sm font-medium px-2 py-1 rounded hover:bg-green-500/20 transition-colors"
                                        >
                                            Télécharger
                                        </button>
                                        {canDeleteFile(file) && (
                                            <button
                                                onClick={() => handleDeleteFile(file)}
                                                disabled={deletingFileId === file.id}
                                                className="text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium px-2 py-1 rounded hover:bg-red-500/20 transition-colors"
                                                title="Supprimer le fichier"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Zone d'upload — visible pour client, prothésiste et admin */}
                        <div className="mt-6 pt-6 border-t border-white/10">
                                <div className="flex items-center mb-4">
                                    <Upload className="w-5 h-5 text-green-400 mr-2" />
                                    <h4 className="text-md font-semibold text-white">Ajouter des fichiers</h4>
                                </div>

                                <FileUploadZone
                                    files={newFiles}
                                    onFilesChange={setNewFiles}
                                    disabled={isUploading}
                                />

                                {uploadError && (
                                    <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                                        <p className="text-sm text-red-400">{uploadError}</p>
                                    </div>
                                )}

                                {uploadSuccess && (
                                    <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                                        <p className="text-sm text-green-400">Fichiers uploadés avec succès !</p>
                                    </div>
                                )}

                                {newFiles.length > 0 && (
                                    <button
                                        onClick={handleUploadFiles}
                                        disabled={isUploading}
                                        className="mt-4 w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                                    >
                                        {isUploading ? (
                                            <div className="flex items-center justify-center space-x-2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                <span>Upload en cours...</span>
                                            </div>
                                        ) : (
                                            `Envoyer ${newFiles.length} fichier${newFiles.length > 1 ? 's' : ''}`
                                        )}
                                    </button>
                                )}
                            </div>
                    </div>

                    </>
                    )}
                    </div>

                    {/* Colonne droite - Discussion */}
                    <div className="space-y-6">
                        <OrderDiscussion
                            order={currentOrder}
                            currentUserType={currentUserType}
                            currentUserName={currentUserName}
                            onSendMessage={handleSendMessage}
                            onNewMessage={(message) => {
                                // Mettre à jour l'état parent quand un message WebSocket arrive
                                if (onWebSocketMessage) {
                                    onWebSocketMessage(currentOrder.id, message);
                                }
                            }}
                        />

                        {currentUserType === 'prosthetist' && (
                            <InternalNotes orderId={currentOrder.id} />
                        )}
                    </div>
                </div>

                {/* Boutons d'action - en bas à droite */}
                {!isEditing && (
                    <div className="flex flex-wrap justify-end gap-2 sm:gap-3 pt-4 mt-4 border-t border-white/10">
                        {currentOrder.status === ORDER_STATUS.PENDING && onDeleteOrder && (
                            <button
                                onClick={handleDeleteClick}
                                className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center space-x-1.5 sm:space-x-2"
                            >
                                <Ban className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                <span>Annuler la commande</span>
                            </button>
                        )}
                        {/* invoice_id : les commandes facturées hors appli n'ont pas de PDF à télécharger */}
                        {['client', 'prosthetist'].includes(currentUserType) && currentOrder.invoice_id && (
                            <button
                                onClick={handleDownloadInvoice}
                                disabled={downloadingInvoice}
                                className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center space-x-1.5 sm:space-x-2"
                            >
                                <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                <span>{downloadingInvoice ? 'Téléchargement…' : 'Télécharger la facture'}</span>
                            </button>
                        )}
                        {userCanEdit && (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 flex items-center space-x-1.5 sm:space-x-2"
                            >
                                <Edit3 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                <span>Modifier</span>
                            </button>
                        )}
                        <button
                            onClick={handleClose}
                            className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm bg-white/15 text-gray-300 rounded-md hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        >
                            Fermer
                        </button>
                    </div>
                )}
            </div>

            {/* Modal de confirmation de suppression */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 overlay-frost flex items-center justify-center z-60 animate-fade-in">
                    <div className="glass-deep border border-white/10 rounded-lg p-6 max-w-md w-full mx-4 animate-modal-in">
                        <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                                    <Ban className="h-5 w-5 text-red-400" />
                                </div>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-medium text-white mb-2">
                                    Confirmer l'annulation
                                </h3>
                                <p className="text-sm text-gray-300 mb-4">
                                    Êtes-vous sûr de vouloir annuler la commande #{currentOrder.id} ?
                                    Cette action est irréversible.
                                </p>
                                <div className="flex space-x-3">
                                    <button
                                        onClick={handleConfirmDelete}
                                        className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
                                    >
                                        Oui, annuler la commande
                                    </button>
                                    <button
                                        onClick={handleCancelDelete}
                                        className="flex-1 bg-white/15 hover:bg-white/20 text-gray-300 px-4 py-2 rounded text-sm font-medium transition-colors"
                                    >
                                        Retour
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de prévisualisation */}
            {previewFile && (
                <FilePreview
                    file={previewFile}
                    onClose={handleClosePreview}
                />
            )}
        </div>
    );
};

export default OrderDetails;